import React, { useState, useEffect, useCallback, useRef } from "react";
import { useLoading } from "../../../context/LoadingContext";
import "../../../styles/general.css";
import "../../../styles/amtpl.css";
import DataTable from "../../Requirement/components/common/ReusableTable";
import SearchFilter from "../../Requirement/components/common/SearchFilter";
import { TextField, TextareaAutosize } from "@mui/material";
import {
  fetchData,
  postQuery,
} from "../../Requirement/services/RequirementServices";
import AlertModal from "../../Requirement/components/common/AlerModel";

const GlobalSettings = () => {
  const { setLoading } = useLoading();
  const [globalSettings, setGlobalSettings] = useState([]);
  const [rowStart, setRowStart] = useState(0);
  const [pageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [searchValues, setSearchValues] = useState({
    searchSettingKey: "",
    searchSettingData: "",
  });
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    SettingKey: "",
    SettingData: "",
  });
  const [errors, setErrors] = useState({});
  const [modal, setModal] = useState({
    show: false,
    type: "info",
    title: "",
    message: "",
    nextAction: null,
    autoClose: null,
  });

  const formRef = useRef(null);

  const handleModalClose = () => {
    const action = modal.nextAction;
    setModal((prev) => ({ ...prev, show: false, nextAction: null }));
    if (action) action();
  };

  const fetchSettings = useCallback(
    async (searchParams = {}, startRow = 0) => {
      setLoading(true);
      try {
        const body = {
          target: "listGlobalSettings",
          userOrder: "SettingKey ASC",
          rowStart: startRow,
          pageSize: pageSize,
          filterTerms: {
            SettingKey: searchParams.searchSettingKey || "",
            SettingData: searchParams.searchSettingData || "",
          },
        };

        const result = await fetchData(body);

        if (result && (result.items || result.statusCode === 200)) {
          const settings = result.items || [];
          console.log("Fetched settings:", settings); // Debug log
          setGlobalSettings(settings);
          setTotalItems(result.total || settings.length);
        } else {
          console.error("Error fetching settings: ", result);
          setGlobalSettings([]);
          setTotalItems(0);
        }
      } catch (error) {
        console.error("Error fetching settings: ", error);
        setGlobalSettings([]);
        setTotalItems(0);
      } finally {
        setLoading(false);
      }
    },
    [setLoading, pageSize]
  );

  const handleSearch = useCallback(
    async (searchParams) => {
      setRowStart(0);
      setCurrentPage(0);
      await fetchSettings(searchParams, 0);
    },
    [fetchSettings]
  );

  const handlePageChange = useCallback(
    async (newPage) => {
      const newRowStart = newPage * pageSize;
      setRowStart(newRowStart);
      setCurrentPage(newPage);
      await fetchSettings(searchValues, newRowStart);
    },
    [fetchSettings, pageSize, searchValues]
  );

  const handleRowClick = (row) => {
    console.log("Row clicked:", row); // Debug log
    setFormData({
      SettingKey: row.SettingKey || "",
      SettingData: row.SettingData || "",
    });
    setErrors({}); // Clear any existing errors
    setShowForm(true);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.SettingKey.trim()) {
      newErrors.SettingKey = "Setting Key is required";
    }

    if (!formData.SettingData.trim()) {
      newErrors.SettingData = "Setting Data is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const saveFormData = async (dataToSave) => {
    setLoading(true);
    try {
      const globalSettingsInput = {
        SettingKey: dataToSave.SettingKey.trim(),
        SettingData: dataToSave.SettingData.trim(),
      };

      const body = {
        target: "updateGlobalSettings",
        globalSettingsInput: globalSettingsInput,
      };

      console.log("body@@", body);

      const result = await postQuery(body);
      if (result) {
        setModal({
          show: true,
          type: "success",
          title: "Success",
          message: `Setting updated successfully!`,
          autoClose: false,
        });
      }

      // Refresh the table data
      await fetchSettings(searchValues, rowStart);
    } catch (error) {
      console.error("Error saving data:", error);
      setModal({
        show: true,
        type: "error",
        title: "Error",
        message: `Failed to update setting.`,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      await saveFormData(formData);
      setShowForm(false);
      setFormData({
        SettingKey: "",
        SettingData: "",
      });
    }
  };

  const handleReset = () => {
    setFormData({
      SettingKey: "",
      SettingData: "",
    });
    setErrors({});
    setShowForm(false);
  };

  const handleRefresh = async () => {
    setSearchValues({
      searchSettingKey: "",
      searchSettingData: "",
    });
    setRowStart(0);
    setCurrentPage(0);
    await fetchSettings({}, 0);
  };

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  // Debug effect to monitor formData changes
  useEffect(() => {
    console.log("FormData updated:", formData);
  }, [formData]);

  const fields = [
    {
      name: "searchSettingKey",
      label: "Setting Key",
      type: "text",
      value: searchValues.searchSettingKey,
      onChange: (value) =>
        setSearchValues((prev) => ({ ...prev, searchSettingKey: value })),
    },
    {
      name: "searchSettingData",
      label: "Setting Data",
      type: "text",
      value: searchValues.searchSettingData,
      onChange: (value) =>
        setSearchValues((prev) => ({ ...prev, searchSettingData: value })),
    },
  ];

  const columns = [
    {
      id: "SettingKey",
      numeric: false,
      label: "Setting Key",
    },
    {
      id: "SettingData",
      numeric: false,
      label: "Setting Data",
      renderCell: (row) => (
        <div
          style={{
            maxWidth: "300px",
            wordBreak: "break-word",
            whiteSpace: "pre-wrap",
          }}
        >
          {row.SettingData || ""}
        </div>
      ),
    },
  ];

  return (
    <div>
      <AlertModal
        type={modal.type}
        title={modal.title}
        message={modal.message}
        showModal={modal.show}
        onClose={handleModalClose}
        buttonText="OK"
        autoClose={modal.autoClose}
      />

      <div className="content">
        <main className="main-content">
          <div className="search-filter4">
            <div className="title4">
              <div className="head-text">Global Settings</div>
            </div>
            <div className="filter4">
              <SearchFilter fields={fields} onSearch={handleSearch} />
            </div>
            <div
              className="head-btn"
              style={{ display: "flex", gap: "10px", alignItems: "center" }}
            >
              <div
                className="submit-btn"
                onClick={handleRefresh}
                style={{ cursor: "pointer" }}
              >
                Refresh
              </div>
            </div>
          </div>

          <div className="table-css">
            <DataTable
              columns={columns}
              data={globalSettings}
              total={totalItems}
              title="Global Settings"
              rowsPerPage={pageSize}
              onRowClick={handleRowClick}
              onPageChange={handlePageChange}
              currentPage={currentPage}
            />
          </div>

          <div className="table-header">
            <div className="header-text">
              <div className="head-text"></div>
            </div>
            <div className="head-btn">
              {showForm && (
                <>
                  <div onClick={handleSubmit} className="submit-btn">
                    Update
                  </div>
                  <div onClick={handleReset} className="submit-btn">
                    Cancel
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Form Section */}
          {showForm && (
            <form
              ref={formRef}
              onSubmit={handleSubmit}
              style={{
                padding: "20px",
                backgroundColor: "#f9f9f9",
                borderRadius: "8px",
                marginTop: "20px",
              }}
            >
              <div className="row">
                <div className="col-md-4">
                  <label
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      color: "#333",
                      fontSize: "14px",
                    }}
                  >
                    Setting Key{" "}
                    {errors.SettingKey && (
                      <span style={{ color: "red" }}>*</span>
                    )}
                  </label>

                  <TextField
                    name="SettingKey"
                    value={formData.SettingKey || ""}
                    onChange={(e) => {
                      console.log("Setting Key changed:", e.target.value);
                      setFormData((prev) => ({
                        ...prev,
                        SettingKey: e.target.value,
                      }));
                    }}
                    error={!!errors.SettingKey}
                    sx={{ backgroundColor: "white", width: "100%" }}
                    disabled={true} // Disable editing key when in edit mode
                  />
                </div>
                <div className="col-md-8">
                  <label
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      color: "#333",
                      fontSize: "14px",
                    }}
                  >
                    Setting Data{" "}
                    {errors.SettingData && (
                      <span style={{ color: "red" }}>*</span>
                    )}
                  </label>
                  <TextareaAutosize
                    value={formData.SettingData || ""}
                    onChange={(e) => {
                      console.log("Setting Data changed:", e.target.value);
                      setFormData((prev) => ({
                        ...prev,
                        SettingData: e.target.value,
                      }));
                    }}
                    style={{
                      width: "100%",
                      minHeight: "120px",
                      backgroundColor: "white",
                      border: errors.SettingData
                        ? "2px solid red"
                        : "1px solid #ccc",
                      borderRadius: "4px",
                      padding: "8px",
                      fontFamily: "inherit",
                      fontSize: "14px",
                      resize: "vertical",
                    }}
                    placeholder="Enter setting data..."
                  />
                  {errors.SettingData && (
                    <div
                      style={{
                        color: "red",
                        fontSize: "12px",
                        marginTop: "4px",
                      }}
                    >
                      {errors.SettingData}
                    </div>
                  )}
                </div>
              </div>
            </form>
          )}
        </main>
      </div>
    </div>
  );
};

export default GlobalSettings;
